import { useEffect } from "react";

import FileDownloadIcon from "@mui/icons-material/FileDownload";
import {
  Alert,
  Button,
  Snackbar,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { usePostHog } from "posthog-js/react";
import { useParams } from "react-router-dom";

import { ErrorTypography } from "components/ErrorTypography";
import { EvotruxDivider } from "components/EvotruxDivider";
import { LoadingWrappedNode } from "components/LoadingWrappedNode";
import { PublicShipmentShareButton } from "components/Shipment/PublicShipmentShareButton";
import { PublicShipmentShareModal } from "components/Shipment/PublicShipmentShareModal";
import { ShipmentHeaderLayout } from "components/ShipmentHeaderLayout";
import { ShipmentStatusBadge } from "components/ShipmentStatusBadge";
import { RefreshWithTextButton } from "components/Shipper/components/ShipmentOverviewExpanded/RefreshWithTextButton";
import { SupplierShipmentDetailTitle } from "components/Shipper/components/SupplierShipments/SupplierShipmentDetailTitle";
import { SupplierShipmentTrackingSection } from "components/Shipper/components/SupplierShipments/SupplierShipmentOverviewExpanded/components/SupplierShipmentTrackingSection";
import { ClientAppEvent } from "constants/index";
import { useDisclosure } from "hooks";
import { useIsMobile } from "hooks/useIsMobile";

import { PublicShipmentDetails } from "./components/PublicShipmentDetails";
import { PublicShipmentExportToPDFModal } from "./components/PublicShipmentExportToPDFModal";
import { PublicShipmentProgress } from "./components/PublicShipmentProgress";
import { PublicShipmentRoute } from "./components/PublicShipmentRoute";
import { PublicShipmentStatusNotice } from "./components/PublicShipmentStatusNotice";
import { PublicShipmentStatusSection } from "./components/PublicShipmentStatusSection";
import { usePublicShipment } from "./hooks/usePublicShipment";

const sectionHeaderType = "h5";

// Statuses for which the public page shows live tracking (route, ETAs, status
// history). Every other resolvable status shows a friendly notice instead.
const TRACKING_STATUSES = new Set<ShipmentStatusValue>([
  "BOOKED",
  "PICKED_UP",
  "DELIVERED",
]);

export function PublicShipmentPage() {
  const params = useParams();
  const { token } = params;

  const { shipment, loading, error, refetch } = usePublicShipment({ token });

  // Keep public shipment pages out of search engines. robots.txt disallows
  // crawling /public/, and this meta tag tells any crawler that does reach the
  // page not to index it or follow its links. Injected at runtime (rather than
  // in index.html) so it only applies while this page is mounted, and removed
  // on unmount so it doesn't leak to the rest of the SPA.
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex,nofollow";
    document.head.appendChild(meta);
    return () => {
      document.head.removeChild(meta);
    };
  }, []);

  const useColumnLayout = useIsMobile({ maxWidth: 1200 });
  const {
    isOpen: refreshToastOpen,
    onOpen: openRefreshToast,
    onClose: closeRefreshToast,
  } = useDisclosure(false);
  const {
    isOpen: shareModalOpen,
    onOpen: openShareModal,
    onClose: closeShareModal,
  } = useDisclosure(false);
  const posthog = usePostHog();

  const handleOpenShareModal = () => {
    if (shipment) {
      posthog?.capture(ClientAppEvent.PUBLIC_SHIPMENT_SHARE_MODAL_OPENED, {
        shipment_id: shipment.info.id,
        source: "public",
      });
    }
    openShareModal();
  };
  const {
    isOpen: exportModalOpen,
    onOpen: openExportModal,
    onClose: closeExportModal,
  } = useDisclosure(false);

  const handleRefresh = async () => {
    try {
      await refetch();
      openRefreshToast();
    } catch {
      // SWR sets `error` for ErrorTypography to surface the failure.
    }
  };

  return (
    <LoadingWrappedNode loading={loading}>
      <ErrorTypography error={error} />

      {shipment && (
        <Stack direction="column" p=".3rem" position="relative">
          <Stack direction="column">
            <ShipmentHeaderLayout
              isColumnLayout={useColumnLayout}
              title={<SupplierShipmentDetailTitle shipment={shipment} />}
              progress={<PublicShipmentProgress shipment={shipment} />}
              actions={
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="center"
                  spacing={1}
                >
                  <PublicShipmentShareButton onClick={handleOpenShareModal} />
                  <Tooltip title="Export this shipment as a PDF">
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      startIcon={<FileDownloadIcon />}
                      onClick={openExportModal}
                      sx={{ textTransform: "none", flexShrink: 0 }}
                    >
                      Export
                    </Button>
                  </Tooltip>
                  <Tooltip title="Refresh">
                    <span>
                      <RefreshWithTextButton onRefresh={handleRefresh} />
                    </span>
                  </Tooltip>
                </Stack>
              }
            />

            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ mt: "1rem" }}
            >
              <Typography variant={sectionHeaderType}>
                Shipment Status
              </Typography>
              <ShipmentStatusBadge
                status={shipment.shipment_status}
                size="small"
              />
            </Stack>
            <EvotruxDivider />
            {TRACKING_STATUSES.has(shipment.shipment_status) ? (
              <>
                <PublicShipmentStatusSection shipment={shipment} />

                <SupplierShipmentTrackingSection
                  dividerComponent={
                    <>
                      <Typography
                        variant={sectionHeaderType}
                        sx={{ mt: "1rem" }}
                      >
                        Shipment Tracking
                      </Typography>
                      <EvotruxDivider />
                    </>
                  }
                  shipment={shipment}
                />

                <Typography variant={sectionHeaderType} sx={{ mt: "1rem" }}>
                  Shipment Details
                </Typography>
                <EvotruxDivider />
                <PublicShipmentDetails shipment={shipment} />

                <Typography variant={sectionHeaderType} sx={{ mt: "1rem" }}>
                  Route
                </Typography>
                <EvotruxDivider />
                <PublicShipmentRoute shipment={shipment} />
              </>
            ) : (
              <PublicShipmentStatusNotice status={shipment.shipment_status} />
            )}
          </Stack>
        </Stack>
      )}
      <Snackbar
        open={refreshToastOpen}
        autoHideDuration={3000}
        onClose={closeRefreshToast}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={closeRefreshToast}
          severity="success"
          sx={{ width: "100%" }}
        >
          Shipment refreshed with the latest information.
        </Alert>
      </Snackbar>
      {shipment && (
        <PublicShipmentShareModal
          open={shareModalOpen}
          onClose={closeShareModal}
          publicToken={shipment.info.public_token}
          shipmentId={shipment.info.id}
          source="public"
        />
      )}
      {shipment && (
        <PublicShipmentExportToPDFModal
          open={exportModalOpen}
          onClose={closeExportModal}
          shipment={shipment}
        />
      )}
    </LoadingWrappedNode>
  );
}
